import React, { useMemo } from 'react';
import queryString from 'query-string';
import { useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { H5, P } from 'components/Typography';
import Button from 'components/Button';
import {
  ERROR_MESSAGE_DEFAULT,
  ERROR_TITLE_DEFAULT
} from 'products/nomad_insurance/constants';
import ResetPassword from './ResetPassword';
import validation from './validation';
import {
  ForgotPasswordContainer,
  Bird,
  ContentWrapper,
  EmailInput,
  StyledSpinner,
  ErrorAlert,
  WarningAlert
} from './styled';
import { resetPasswordEmail, resendMemberInvitation } from './state';

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { isLoading, isSent, resendInvitation, error } = useSelector(
    (state) => state.global.forgotPassword
  );

  const { token, email } = queryString.parse(location.search);

  const formik = useFormik({
    initialValues: useMemo(
      () => ({
        email
      }),
      [] // eslint-disable-line
    ),
    validateOnMount: true,
    validationSchema: validation,
    onSubmit: ({ email }) => dispatch(resetPasswordEmail(email))
  });

  const renderError = () => {
    switch (error?.type) {
      case 'RESET__NO_USER':
      case 'NO_USER':
        return (
          <ErrorAlert variant="warning" label="No user found">
            We didn’t find a user with this email. Any chance you signed up using
            Facebook?
          </ErrorAlert>
        );
      case 'RESET__INACTIVE_USER':
        return (
          <WarningAlert variant="warning" label="Inactive user">
            This user is not activated and you cannot set a password.
          </WarningAlert>
        );
      case 'RESET__INACTIVE_COMPANY_MEMBER':
        return (
          <WarningAlert variant="warning" label="Inactive user">
            <div>
              This user have not been activated. Did you receive the member invitation by
              email sent by your company? You need to activate and set up your user
              profile before you can log in and/or reset password.
            </div>
            <div>Do you want us to resend the invitation?</div>
            <Button
              variant="primary"
              onClick={() => dispatch(resendMemberInvitation(formik?.values?.email))}
            >
              Resend invitation
            </Button>
          </WarningAlert>
        );
      default:
        return (
          <ErrorAlert variant="danger" label={ERROR_TITLE_DEFAULT}>
            {ERROR_MESSAGE_DEFAULT}
          </ErrorAlert>
        );
    }
  };

  if (token && email) {
    return <ResetPassword token={token} email={email} />;
  }

  return (
    <ForgotPasswordContainer>
      <Bird />
      <ContentWrapper>
        <H5>Reset password</H5>
        {error && renderError()}
        {!isLoading && !isSent && (
          <>
            <EmailInput
              label="Email"
              variant="light"
              name="email"
              formik={formik}
              inputMode="email"
            />
            <Button disabled={!formik.isValid} onClick={formik.submitForm}>
              Send
            </Button>
          </>
        )}
        {isLoading && !isSent && <StyledSpinner />}
        {isSent && !isLoading && (
          <P>
            {resendInvitation
              ? `We have resent you the member invitation email. If you do not see it, you may
            want to check your spam folder.`
              : `We have sent you a confirmation email. If you do not see it, you may want to
            check your spam folder`}
            .
          </P>
        )}
      </ContentWrapper>
    </ForgotPasswordContainer>
  );
};

export default ForgotPassword;
